/**
 * Map Asset System Types
 * 
 * Types for static map objects like trees, rocks, buildings, etc.
 * These are placeable objects that exist in the game world with
 * collision support and efficient instancing.
 * 
 * Supports compound collisions for rigged models with multiple parts.
 */

import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";

/**
 * Map asset categories for organization
 */
export enum MapAssetCategory {
    VEGETATION = "vegetation",      // Trees, bushes, grass
    ROCK = "rock",                  // Rocks, boulders, cliffs
    STRUCTURE = "structure",        // Buildings, fences, bridges
    PROP = "prop",                  // Decorative items, furniture
    INTERACTIVE = "interactive",    // Chests, doors, switches
    COLLECTIBLE = "collectible",    // Items that can be picked up
}

/**
 * Collision generation mode for map assets
 */
export enum CollisionMode {
    /** No collision - purely visual */
    NONE = "none",
    /** Generate box collider from bounding box */
    BOUNDING_BOX = "bounding_box",
    /** Generate sphere collider from bounding sphere */
    BOUNDING_SPHERE = "bounding_sphere",
    /** Generate capsule collider (good for tall objects) */
    CAPSULE = "capsule",
    /** Generate cylinder collider (good for tree trunks) */
    CYLINDER = "cylinder",
    /** Use exact mesh collision (expensive) */
    MESH = "mesh",
    /** Use convex hull collision (moderate cost) */
    CONVEX_HULL = "convex_hull",
    /** Custom dimensions provided */
    CUSTOM = "custom",
}

/**
 * Custom collision dimensions
 */
export interface CustomCollisionDimensions {
    /** Collision shape type */
    shapeType: PhysicsShapeType;
    /** Width (X axis) */
    width?: number;
    /** Height (Y axis) */
    height?: number;
    /** Depth (Z axis) */
    depth?: number;
    /** Radius (for sphere/capsule/cylinder) */
    radius?: number;
    /** Offset from model center */
    offset?: { x: number; y: number; z: number };
}

/**
 * Map asset definition - describes a placeable map object
 */
export interface MapAssetDefinition {
    /** Unique identifier */
    id: string;
    /** Display name */
    name: string;
    /** Path to GLB model (relative to assets/) */
    modelPath: string;
    /** Asset category */
    category: MapAssetCategory;
    /** Default scale */
    scale?: number;
    /** Collision configuration */
    collision?: {
        /** How to generate collision */
        mode: CollisionMode;
        /** Custom dimensions (if mode is CUSTOM) */
        customDimensions?: CustomCollisionDimensions;
        /** Friction coefficient */
        friction?: number;
        /** Restitution (bounciness) */
        restitution?: number;
        /** Collision group */
        collisionGroup?: number;
        /** Collision mask */
        collisionMask?: number;
        /** 
         * Enable compound collision from model parts
         * When true, creates separate collision shapes for each mesh part
         * Useful for rigged models with distinct parts (arms, branches, etc.)
         */
        useCompoundCollision?: boolean;
        /**
         * Only use specific mesh parts for collision (by name pattern)
         * Use regex patterns like "trunk|branch" to match mesh names
         */
        collisionMeshFilter?: string;
        /**
         * Enable debug visualization of collision shapes
         */
        debugVisualize?: boolean;
    };
    /** Whether to cast shadows */
    castShadows?: boolean;
    /** Whether to receive shadows */
    receiveShadows?: boolean;
    /** Optional metadata */
    metadata?: Record<string, unknown>;
}

/**
 * Placement data for a map asset instance
 */
export interface MapAssetPlacement {
    /** Reference to asset definition ID */
    assetId: string;
    /** World position */
    position: Vector3;
    /** Rotation (optional, defaults to identity) */
    rotation?: Quaternion;
    /** Scale override (optional) */
    scale?: number;
    /** Random Y rotation (optional) */
    randomYRotation?: boolean;
    /** Unique instance ID (auto-generated if not provided) */
    instanceId?: string;
    /** Additional instance metadata */
    metadata?: Record<string, unknown>;
}

/**
 * Map asset instance - a placed map asset in the world
 */
export interface MapAssetInstance {
    /** Unique instance ID */
    id: string;
    /** Asset definition ID */
    assetId: string;
    /** The root transform node */
    rootNode: import("@babylonjs/core/Meshes/transformNode").TransformNode;
    /** All meshes in this instance */
    meshes: import("@babylonjs/core/Meshes/abstractMesh").AbstractMesh[];
    /** Physics aggregate (if collision enabled) - primary collision body */
    physicsAggregate: import("@babylonjs/core/Physics/v2/physicsAggregate").PhysicsAggregate | null;
    /** Additional physics aggregates for compound collision parts */
    compoundAggregates?: import("@babylonjs/core/Physics/v2/physicsAggregate").PhysicsAggregate[];
    /** Debug visualization meshes (if debug enabled) */
    debugMeshes?: import("@babylonjs/core/Meshes/mesh").Mesh[];
    /** World position */
    position: Vector3;
    /** Current scale */
    scale: number;
    /** Whether the instance is enabled/visible */
    enabled: boolean;
}

/**
 * Map asset batch placement configuration
 */
export interface MapAssetBatchConfig {
    /** Asset ID to place */
    assetId: string;
    /** Array of positions */
    positions: Vector3[];
    /** Apply random Y rotation to each */
    randomizeRotation?: boolean;
    /** Scale range [min, max] for random scaling */
    scaleRange?: [number, number];
    /** Terrain system reference for height adjustment */
    adjustToTerrain?: boolean;
}

/**
 * Event types for map asset system
 */
export type MapAssetEventType = 
    | "asset_loaded"
    | "instance_created"
    | "instance_destroyed"
    | "collision_detected";

/**
 * Map asset event data
 */
export interface MapAssetEvent {
    type: MapAssetEventType;
    assetId?: string;
    instanceId?: string;
    data?: unknown;
    timestamp: number;
}

/**
 * Map asset event handler
 */
export type MapAssetEventHandler = (event: MapAssetEvent) => void;
